import axios from 'axios'
import { promiseCache } from './promiseCache'
import { API_URL } from '../config'
import permissionManager from '../services/permissions/PermissionManager'

const setAuthTokenHeader = () => {
  const token = window.localStorage.getItem('token')
  if (!token) {
    window.location = '/';
    return null;
  }
  return {
    Authorization: `Bearer ${token}`,
  };
};

const includeUserId = () => {
  return window.localStorage.getItem('userId');
};

const apiClient = axios.create({
  baseURL: API_URL,
  withCredentials: false,
});

apiClient.interceptors.response.use(
  (response) => {
    permissionManager.updatePermissionsFromAPIResponse(response.data)
    return response
  },
  (error) => {
    if (error.response?.status) {
      return Promise.reject(error)
    }
  }
);

const remoteFetch = async () => {
  const url =
    `${API_URL}/clients?dk=` +
    encodeURIComponent(window.location.host) +
    '&app=proximity';
  return apiClient.get(url)
};

const fetchClientInfo = async () => {
  const proxy = promiseCache(remoteFetch)
  const res = await proxy()
  if (!res?.data?.data) {
    window.location = '/BadDomain.html';
  }
  return res.data.data;
};

/**
 * Fetch method to handle API requests with optional authentication and custom headers.
 * @param {string} url - The endpoint URL to fetch.
 * @param {Object} data - The data to send with the request (for POST/PUT methods).
 * @param {string} method - The HTTP method to use (GET, POST, PUT, DELETE).
 * @param {boolean} isAuthToken - Whether to include the authentication token in the headers.
 * @param {Object} header - Additional headers to include in the request.
 * @param {Object} params - Query parameters to include in the request.
 * @param {boolean} isNewEndpoint - Whether the endpoint is a new one that requires a different header structure.
 * @param {Object} config - Additional configuration options for the request.
 * @param {AbortSignal} config.signal - An AbortSignal to cancel the request if needed.
 * @returns {Promise<axios.AxiosResponse<any>>}
 */
export const fetchMethod = async (
  url,
  data,
  method,
  isAuthToken,
  header,
  params,
  isNewEndpoint = false,
  config = {}
) => {
  const { signal } = config;

  const dicKey = 'dk_' + window.location.host

  let client = window.localStorage.getItem(dicKey)
  let token = window.localStorage.getItem('token')

  if (!client) {
    client = await fetchClientInfo()

    if (client) {
      client = JSON.stringify(client)
      window.localStorage.setItem(dicKey, client)
    }
  }

  let fullheaders = {}
  const clientObj = JSON.parse(client)
  const clientKey = clientObj ? clientObj.key : null
  let authheader
  if (
    isNewEndpoint ||
    ['Assets', 'parties', 'services', 'search', 'industries', 'dynamicfields'].includes(url.split('/')[1])
  ) {
    authheader = {
      'X-Client-Key': clientKey,
      Authorization: `Bearer ${token}`,
    }
  } else {
    authheader = { 'X-Client-Key': clientKey }
  }

  if (header) {
    fullheaders = { ...fullheaders, ...header }
  } else {
    fullheaders = {
      ...fullheaders,
      'Access-Control-Allow-Headers': '*',
      'Access-Control-Allow-Origin': '*',
      'Access-Control-Allow-Methods': '*',
    }
  }

  fullheaders = { ...fullheaders, ...authheader }

  return apiClient({
    url,
    method,
    data,
    ...(params && { params }),
    headers: isAuthToken
      ? {
          ...setAuthTokenHeader(),
          userId: includeUserId(),
          ...fullheaders,
        }
      : fullheaders,
    signal,
  })
}
